import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from 'react-router-dom';
import TurnstileWidget from "../common/TurnstileWidget";
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import AuthLayout from "../layouts/AuthLayout";
import { userService } from "../../_services";
import Signupleft from "../../images/signupleft.webp";
import signupcheck from "../../images/savecheck.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { stripEmailTracking } from "../../utils/stripEmailTracking";

const FLOAT_LABEL =
    'absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-3 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none';

function RuserSetup() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        Fname: '',
        Lname: '',
        Phone: '',
        Email: '',
        UserName: '',
        Password: '',
        ConfirmPassword: '',
    });
    const [dataValidated, setDataValidated] = useState(false);
    const [loadingText, setLoadingText] = useState('Please wait while your request is processing');
    const [userId, setUserId] = useState('');
    const [accessCode, setAccessCode] = useState('');
    const [passcode, setPasscode] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [userUpdated, setUserUpdated] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileError, setTurnstileError] = useState(false);

    useEffect(() => {
        const params = new URL(window.location.href).searchParams;
        const token = stripEmailTracking(params.get('accesscode'));
        const username = stripEmailTracking(params.get('username'));
        const pc = stripEmailTracking(params.get('passcode'));
        setAccessCode(token);
        setPasscode(pc);
        verifyUser(token, username, pc);
    }, []);

    const verifyUser = async (token, username, pc) => {
        try {
            const apidata = await userService.verifyuser_organic(token, username, pc);
            if (apidata.length > 0 && apidata[0].statusMessage === "Success") {
                const value = apidata[0];
                setFormData(prev => ({
                    ...prev,
                    Fname: value.fname || '',
                    Lname: value.lname || '',
                    Phone: value.phone || '',
                    Email: value.email || '',
                }));
                setUserId(value.userId || '');
                setDataValidated(true);
            } else {
                setDataValidated(false);
                setLoadingText("Invalid Request! Please Click below to signup");
            }
        } catch {
            setDataValidated(false);
            setLoadingText("Invalid Request! Please Click below to signup");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "Fname" || name === "Lname") {
            if (value !== "" && !/^[a-zA-Z]+$/.test(value)) return;
        }
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!agreeTerms) return;

        if (!turnstileToken) {
            setTurnstileError(true);
            return;
        }

        const newErrors = {};
        const trimmedUsername = formData.UserName.trim();

        // Async: username filter check
        try {
            const usernameCheck = await userService.GetUserNameFilter(trimmedUsername);
            if (usernameCheck.length > 0 && usernameCheck[0].code !== '0') {
                newErrors.UserName = usernameCheck[0].message;
            }
        } catch { /* ignore */ }

        // Async: phone validation
        try {
            const phoneCheck = await userService.GetPhoneNoValidate(formData.Phone);
            if (phoneCheck[0].code !== "1") {
                newErrors.Phone = 'Invalid Phone Number';
            } else if (phoneCheck[0].message) {
                setFormData(prev => ({ ...prev, Phone: phoneCheck[0].message }));
            }
        } catch { /* ignore */ }

        // Sync validations
        if (formData.Fname.length < 3) newErrors.Fname = 'First Name must be at least 3 characters long';
        if (formData.Lname.length < 2) newErrors.Lname = 'Last Name must be at least 2 characters long';
        if (formData.Phone.replace(/[- )(]/g, '').length !== 10) newErrors.Phone = 'Phone Number must be 10 digits';
        if (formData.Email.length < 1) newErrors.Email = 'Enter your email';
        if (!userService.ValidEmailRegex(formData.Email) && formData.Email !== '') newErrors.Email = 'Invalid Email';
        if (trimmedUsername.length < 6) newErrors.UserName = 'Username must be at least 6 characters long';
        if (['user', 'rxh', 'temp'].some(kw => trimmedUsername.includes(kw))) newErrors.UserName = 'This Username not allowed';
        if (/^[0-9]+$/.test(trimmedUsername)) newErrors.UserName = 'This Username not allowed';
        if (trimmedUsername.includes(' ')) newErrors.UserName = 'Username does not allow space';
        if (formData.Password.length < 6) newErrors.Password = 'Password must be at least 6 characters long';
        if (formData.Password !== formData.ConfirmPassword) newErrors.ConfirmPassword = 'Password & Confirm Password must match';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsLoading(true);
        try {
            const apidata = await userService.update_orguser(
                userId,
                trimmedUsername,
                accessCode,
                passcode,
                formData.Fname,
                formData.Lname,
                formData.Email,
                formData.Phone,
                formData.Password
            );
            if (apidata.length > 0) {
                if (apidata[0].code !== '-1') {
                    setUserUpdated(true);
                } else {
                    if (apidata[0].statusMessage === 'Phone no is already exist') {
                        setErrors({ Phone: 'Phone Number already exists' });
                        setFormData(prev => ({ ...prev, Phone: '' }));
                    } else {
                        setErrors({ UserName: apidata[0].statusMessage });
                        setFormData(prev => ({ ...prev, UserName: '' }));
                    }
                }
            }
        } catch (error) {
            console.error('Update error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Redirect if already logged in
    if (!userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/Affiliate_Url" replace />;
    }

    // ── Success screen ──────────────────────────────────────────────────────────
    if (userUpdated) {
        return (
            <AuthLayout imageSrc={Signupleft}>
                <div className="flex flex-col items-center text-center md:items-start md:text-left space-y-4">
                    <img src={signupcheck} alt="Success" className="w-20 h-auto mx-auto md:mx-0" />
                    <p className="text-lg font-semibold text-foreground">Updated successfully</p>
                    <Button variant="secondary" size="lg" className="w-auto" onClick={() => navigate('/login')}>
                        Sign In Now
                    </Button>
                </div>
            </AuthLayout>
        );
    }

    // ── Loading / Invalid screen ────────────────────────────────────────────────
    if (!dataValidated) {
        return (
            <AuthLayout imageSrc={Signupleft}>
                <div className="flex flex-col items-center text-center md:items-start md:text-left space-y-4">
                    {loadingText === "Please wait while your request is processing" && (
                        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto md:mx-0" />
                    )}
                    <p className="text-md text-foreground opacity-90">{loadingText}</p>
                    {loadingText !== "Please wait while your request is processing" && (
                        <Button variant="secondary" size="lg" className="w-auto" onClick={() => navigate('/signup')}>
                            Sign Up here
                        </Button>
                    )}
                </div>
            </AuthLayout>
        );
    }

    // ── Main form ───────────────────────────────────────────────────────────────
    return (
        <AuthLayout imageSrc={Signupleft} title="Set Details" description="Complete your account setup">
            <form onSubmit={handleSubmit} className="space-y-4">

                <div className="relative">
                    <Input
                        id="Fname"
                        name="Fname"
                        value={formData.Fname}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        placeholder=" "
                        className={cn('peer h-12', errors.Fname && 'border-destructive')}
                    />
                    <Label htmlFor="Fname" className={FLOAT_LABEL}>First Name</Label>
                </div>
                {errors.Fname && <p className="text-sm font-medium text-destructive">{errors.Fname}</p>}

                <div className="relative">
                    <Input
                        id="Lname"
                        name="Lname"
                        value={formData.Lname}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        placeholder=" "
                        className={cn('peer h-12', errors.Lname && 'border-destructive')}
                    />
                    <Label htmlFor="Lname" className={FLOAT_LABEL}>Last Name</Label>
                </div>
                {errors.Lname && <p className="text-sm font-medium text-destructive">{errors.Lname}</p>}

                <div className="relative">
                    <Input
                        id="Phone"
                        name="Phone"
                        value={formData.Phone}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        placeholder=" "
                        className={cn('peer h-12', errors.Phone && 'border-destructive')}
                    />
                    <Label htmlFor="Phone" className={FLOAT_LABEL}>Phone Number</Label>
                </div>
                {errors.Phone && <p className="text-sm font-medium text-destructive">{errors.Phone}</p>}

                <div className="relative">
                    <Input
                        id="Email"
                        name="Email"
                        type="email"
                        value={formData.Email}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        placeholder=" "
                        className={cn('peer h-12', errors.Email && 'border-destructive')}
                    />
                    <Label htmlFor="Email" className={FLOAT_LABEL}>Email</Label>
                </div>
                {errors.Email && <p className="text-sm font-medium text-destructive">{errors.Email}</p>}

                <div className="relative">
                    <Input
                        id="UserName"
                        name="UserName"
                        value={formData.UserName}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        placeholder=" "
                        className={cn('peer h-12', errors.UserName && 'border-destructive')}
                    />
                    <Label htmlFor="UserName" className={FLOAT_LABEL}>Username</Label>
                </div>
                {errors.UserName && <p className="text-sm font-medium text-destructive">{errors.UserName}</p>}

                <div className="relative">
                    <Input
                        id="Password"
                        name="Password"
                        type={showPassword ? "text" : "password"}
                        value={formData.Password}
                        onChange={handleInputChange}
                        maxLength={30}
                        disabled={isLoading}
                        placeholder=" "
                        className={cn('peer h-12 pr-10', errors.Password && 'border-destructive')}
                    />
                    <Label htmlFor="Password" className={FLOAT_LABEL}>Password</Label>
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                        tabIndex={-1}
                    >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                </div>
                {errors.Password && <p className="text-sm font-medium text-destructive">{errors.Password}</p>}

                <div className="relative">
                    <Input
                        id="ConfirmPassword"
                        name="ConfirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={formData.ConfirmPassword}
                        onChange={handleInputChange}
                        maxLength={30}
                        disabled={isLoading}
                        placeholder=" "
                        className={cn('peer h-12 pr-10', errors.ConfirmPassword && 'border-destructive')}
                    />
                    <Label htmlFor="ConfirmPassword" className={FLOAT_LABEL}>Confirm Password</Label>
                    <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground"
                        tabIndex={-1}
                    >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                </div>
                {errors.ConfirmPassword && (
                    <p className="text-sm font-medium text-destructive">{errors.ConfirmPassword}</p>
                )}

                <div className="flex flex-row items-start space-x-3 space-y-0 pt-1">
                    <Checkbox
                        id="terms"
                        checked={agreeTerms}
                        onCheckedChange={setAgreeTerms}
                        disabled={isLoading}
                    />
                    <div className="space-y-1 leading-none">
                        <label htmlFor="terms" className="text-sm font-normal text-foreground cursor-pointer leading-relaxed">
                            By clicking submit you agree to our{' '}
                            <a href="/terms/" target="_blank" rel="noreferrer" className="text-accent hover:no-underline hover:text-foreground font-medium">
                                Terms and Conditions
                            </a>
                            .
                        </label>
                    </div>
                </div>

                <div className="w-full">
                    <TurnstileWidget
                        onSuccess={(token) => { setTurnstileToken(token); setTurnstileError(false); }}
                        onError={() => { setTurnstileToken(''); setTurnstileError(true); }}
                        onExpire={() => { setTurnstileToken(''); setTurnstileError(true); }}
                    />
                    {turnstileError && (
                        <p className="text-sm font-medium text-destructive mt-1">
                            Please complete the verification above
                        </p>
                    )}
                </div>

                <Button
                    type="submit"
                    variant="secondary"
                    size="lg"
                    className="w-auto"
                    disabled={!agreeTerms || isLoading}
                >
                    {isLoading
                        ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Submitting</>
                        : 'Submit'
                    }
                </Button>

            </form>
        </AuthLayout>
    );
}

export default RuserSetup;
